import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ApiError } from '@/api/envelope';
import { ROUTES } from '@/app/routes';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { FilterBar, type FilterDefinition } from '@/components/ui/FilterBar';
import { PageHeader } from '@/components/ui/PageHeader';
import { testIdAttr, testIdPart } from '@/lib/testIds';
import { useDebounced } from '@/lib/useDebounced';

import { CategoryTabs } from './CategoryTabs';
import { DocumentsTable } from './DocumentsTable';
import { EditDocumentDrawer } from './EditDocumentDrawer';
import { RemoveDocumentDialog } from './RemoveDocumentDialog';
import { ReplaceFileDialog } from './ReplaceFileDialog';
import { UploadDocumentDialog } from './UploadDocumentDialog';
import { TRANSITIONS, type DocumentActionId } from './documentActions';
import {
  ALL_TAB,
  LIFECYCLE_WORDS,
  UNCATEGORIZED_TAB,
  firstScanPending,
  placementWord,
  type CategoryTab,
  type DocumentRow,
  type Lifecycle,
} from './documentLabels';
import { detailKey, listKey } from './keys';
import {
  PAGE_SIZE,
  fetchDocument,
  useDocumentsList,
  useDocumentsOrganizationId,
} from './useDocuments';
import { useDocumentLink } from './useDocumentLink';
import { useDocumentTransition } from './useDocumentWrites';
import {
  RANGES,
  useDocumentsParams,
  type DocumentsParams,
  type Placement,
  type Range,
  type Status,
} from './useDocumentsParams';
import { useDocumentsWritable } from './useDocumentsWritable';

const FILTERS: FilterDefinition[] = [
  {
    name: 'placement',
    label: 'ARC placement',
    options: [
      { value: 'in', label: placementWord(true) },
      { value: 'not_in', label: placementWord(false) },
    ],
  },
  {
    name: 'status',
    label: 'Status',
    options: (Object.keys(LIFECYCLE_WORDS) as Lifecycle[]).map((value) => ({
      value,
      label: LIFECYCLE_WORDS[value],
    })),
  },
];

/** The tabs before the first reply, so the row is drawn at its height. */
const NO_TABS: CategoryTab[] = [
  { id: ALL_TAB, name: 'All Documents', count: 0 },
  { id: UNCATEGORIZED_TAB, name: 'Uncategorized', count: 0 },
];

const GONE_CATEGORY = 'That category no longer exists, so every document is shown.';

/** Whether a search, a filter or a tab narrows the list. */
function isFiltered(params: DocumentsParams): boolean {
  return (
    params.search.trim() !== '' ||
    params.placement !== '' ||
    params.status !== '' ||
    params.category !== ALL_TAB
  );
}

interface DocumentsPageProps {
  /**
   * True at `/documents/upload` (P2): the library with the upload dialog
   * already open, which closes back to `/documents`. The side-nav action and
   * a deep link come in this way; the header button opens the same dialog in
   * place.
   */
  uploadOpen?: boolean;
}

/**
 * The Document Library (PB1, PB3, PB5, PB11): the category tabs, the filter
 * bar, the table, the upload dialog, and the scan followed while it runs.
 *
 * The view lives in the address (P5): every change goes through
 * `useDocumentsParams`, and the list is keyed on what it reads back, so
 * there is one source of what is shown. Only the search box keeps a draft of
 * its own, settled into the address after a pause in typing.
 */
export function DocumentsPage({ uploadOpen = false }: DocumentsPageProps) {
  const { writable, statusForbids, statusWord } = useDocumentsWritable();
  const organizationId = useDocumentsOrganizationId();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [params, change] = useDocumentsParams();

  // A fresh dialog for every upload, so nothing from the last one is shown.
  // Open from the start when the address asked for it, for somebody who may.
  const [uploading, setUploading] = useState(0);
  const [uploadShown, setUploadShown] = useState(uploadOpen);
  const closeUpload = useCallback(() => {
    setUploadShown(false);
    // The address said "upload"; now that it is over, say "library".
    if (uploadOpen) void navigate(ROUTES.documents.library, { replace: true });
  }, [uploadOpen, navigate]);
  // Somebody who cannot write has nothing to do at /documents/upload (P15).
  useEffect(() => {
    if (uploadOpen && !writable)
      void navigate(ROUTES.documents.library, { replace: true });
  }, [uploadOpen, writable, navigate]);

  // What is typed, settled into the address after a pause so one request is
  // made for a word, not one per letter (scalability rule 2).
  const [draft, setDraft] = useState(params.search);
  const settled = useDebounced(draft, 300);
  useEffect(() => {
    if (settled !== params.search) change({ search: settled });
    // Only a settled draft writes the address; a changed address is read below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settled]);
  // The address changed from outside - the back button, Clear - so the box follows.
  const [seenSearch, setSeenSearch] = useState(params.search);
  if (params.search !== seenSearch) {
    setSeenSearch(params.search);
    setDraft(params.search);
  }

  const list = useDocumentsList(params);

  // A category in the address the backend refuses (422 naming category_id):
  // say so once and show every document (PB3). The alert is set while
  // rendering, as a derived fact about the error just seen; the address change
  // is a side effect, so it waits for the effect.
  const [goneCategory, setGoneCategory] = useState(false);
  const [seenError, setSeenError] = useState<unknown>(null);
  const gone =
    list.error instanceof ApiError &&
    list.error.status === 422 &&
    (list.error.apiError ?? '').includes('category_id') &&
    params.category !== ALL_TAB;
  if (list.error !== seenError) {
    setSeenError(list.error);
    if (gone) setGoneCategory(true);
  }
  useEffect(() => {
    if (gone) change({ category: ALL_TAB });
  }, [gone, change]);

  /*
   * A row that was Scanning and is no longer listed either cleared and moved
   * to another view, was removed by hand, or was rejected by the scan - and
   * only the last needs saying, because the row has left the default list and
   * the person would otherwise never know (P10, C3). One detail read per
   * vanished id tells them apart.
   *
   * Each Scanning row is remembered with the view it was seen in, and only a
   * later reply for that same view can say it left: a turned page or another
   * tab lists other rows, which is not a row leaving. The view's key names
   * the organization, so nothing seen in one is judged in another.
   */
  const scanning = useRef(new Map<string, { title: string; view: string }>());
  const [rejected, setRejected] = useState<string[]>([]);
  useEffect(() => {
    const rows = list.data?.rows;
    if (!rows || list.isPlaceholderData) return;
    const view = JSON.stringify(listKey(organizationId, params));
    const listed = new Set(rows.map((row) => row.id));
    const vanished = [...scanning.current].filter(
      ([id, seen]) => seen.view === view && !listed.has(id),
    );
    for (const row of rows) {
      if (firstScanPending(row)) {
        scanning.current.set(row.id, { title: row.title, view });
      } else {
        scanning.current.delete(row.id);
      }
    }
    for (const [id, seen] of vanished) {
      scanning.current.delete(id);
      queryClient
        .fetchQuery({
          queryKey: detailKey(organizationId, id, params.range),
          queryFn: () => fetchDocument(id, params.range),
        })
        .then((row) => {
          if (row.scanStatus === 'INFECTED' || row.scanStatus === 'FAILED') {
            setRejected((titles) => [...titles, seen.title]);
          }
        })
        .catch((error: unknown) => {
          // Not known yet, so asked again with the view's next reply - unless
          // the document is gone, which no later read will change.
          if (!(error instanceof ApiError && error.status === 404)) {
            scanning.current.set(id, seen);
          }
        });
    }
    // Judged once per settled reply; the view it belongs to is read with it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list.data, list.isPlaceholderData]);

  const tabs = list.data?.tabs ?? NO_TABS;
  const rows = list.data?.rows ?? [];
  const total = list.data?.pagination.total ?? 0;
  const filtered = isFiltered(params);
  const nothingAtAll =
    list.isSuccess && !list.isPlaceholderData && total === 0 && !filtered;

  // A page past the end - its last row removed, or an old link - comes back
  // empty while the total says there are rows: go to the last page there is.
  const pastTheEnd =
    list.isSuccess &&
    !list.isPlaceholderData &&
    rows.length === 0 &&
    total > 0 &&
    params.page > 1;
  useEffect(() => {
    if (pastTheEnd) change({ page: Math.max(1, Math.ceil(total / PAGE_SIZE)) });
  }, [pastTheEnd, total, change]);

  // The row menu's actions (P11): Edit opens the drawer, Open and Download
  // fetch a link, the lifecycle moves go straight to the backend with a toast,
  // Replace file and Remove ask first. The row is kept while a dialog fades.
  const [editing, setEditing] = useState<string | null>(null);
  const [replacing, setReplacing] = useState<DocumentRow | null>(null);
  const [replaceOpen, setReplaceOpen] = useState(false);
  // A fresh dialog for every replace, so no file chosen for one document is
  // already chosen for the next.
  const [replaceCount, setReplaceCount] = useState(0);
  const [removing, setRemoving] = useState<DocumentRow | null>(null);
  const [removeOpen, setRemoveOpen] = useState(false);
  const transition = useDocumentTransition();
  const { mutate: move } = transition;
  const link = useDocumentLink();
  const onAction = useCallback<(action: DocumentActionId, row: DocumentRow) => void>(
    (action, row) => {
      const to = TRANSITIONS[action];
      if (to) move({ row, to });
      else if (action === 'edit') setEditing(row.id);
      else if (action === 'open') void link.open(row.id);
      else if (action === 'download') void link.download(row.id);
      else if (action === 'replace') {
        setReplacing(row);
        setReplaceCount((n) => n + 1);
        setReplaceOpen(true);
      } else if (action === 'remove') {
        setRemoving(row);
        setRemoveOpen(true);
      }
    },
    [move, link],
  );
  // Stable, so a keystroke in the search box does not redraw the memoised
  // table through a fresh callback (scalability rule 4).
  const { refetch } = list;
  const retry = useCallback(() => void refetch(), [refetch]);
  const turnPage = useCallback((page: number) => change({ page }), [change]);

  function clear() {
    setDraft('');
    change({ search: '', placement: '', status: '' });
  }

  const uploadButton = (label: string, testId: string) =>
    writable ? (
      <Button
        onClick={() => {
          setUploading((n) => n + 1);
          setUploadShown(true);
        }}
        data-testid={testId}
      >
        {label}
      </Button>
    ) : null;

  return (
    <Box data-testid="documents-page">
      <PageHeader
        title="Document Library"
        description="Upload, organize and manage the PDF, PNG and JPEG documents your organization publishes."
        action={uploadButton('Upload Document', 'documents-upload-button')}
      />

      {statusForbids ? (
        <Alert
          severity="warning"
          role="alert"
          data-testid="documents-read-only-alert"
          sx={{ mb: 3, maxWidth: 760 }}
        >
          This organization is {statusWord}, so its documents cannot be changed. Contact
          Activist Technologies to discuss it.
        </Alert>
      ) : null}

      {goneCategory ? (
        <Alert
          severity="info"
          role="alert"
          onClose={() => setGoneCategory(false)}
          data-testid="documents-gone-category-alert"
          sx={{ mb: 2, maxWidth: 760 }}
        >
          {GONE_CATEGORY}
        </Alert>
      ) : null}

      {rejected.length > 0 ? (
        <Alert
          severity="error"
          role="alert"
          onClose={() => setRejected([])}
          data-testid="documents-rejected-alert"
          sx={{ mb: 2, maxWidth: 760 }}
        >
          {rejected.map((title) => `“${title}”`).join(', ')}{' '}
          {rejected.length === 1 ? 'was' : 'were'} rejected by the malware scan and
          removed. Upload the file again, or find it under the Removed status.
        </Alert>
      ) : null}

      <CategoryTabs
        tabs={tabs}
        value={params.category}
        onChange={(category) => change({ category })}
        loading={!list.data}
        testId="documents-tabs"
      />

      <FilterBar
        testId="documents-filters"
        searchValue={draft}
        onSearchChange={setDraft}
        searchPlaceholder="Search documents"
        filters={FILTERS}
        values={{ placement: params.placement, status: params.status }}
        onFilterChange={(name, value) =>
          change(
            name === 'placement'
              ? { placement: value as Placement }
              : { status: value as Status },
          )
        }
        extra={
          <TextField
            select
            size="small"
            label="Range"
            value={String(params.range)}
            onChange={(event) => change({ range: Number(event.target.value) as Range })}
            slotProps={{
              select: {
                SelectDisplayProps: testIdAttr('documents-filters-range-select'),
              },
            }}
            sx={{ minWidth: 160 }}
          >
            {RANGES.map((days) => (
              <MenuItem
                key={days}
                value={String(days)}
                data-testid={`documents-filters-range-option-${testIdPart(days)}`}
              >
                Last {days} days
              </MenuItem>
            ))}
          </TextField>
        }
        onClear={clear}
      />

      {list.isError && list.data && !goneCategory ? (
        // A failed refresh keeps the rows already shown and says so, with a way
        // to try again; only a list that never loaded gives way to the error.
        <Alert
          severity="warning"
          role="alert"
          action={
            <Button
              variant="secondary"
              size="sm"
              onClick={retry}
              data-testid="documents-refresh-retry-button"
            >
              Try again
            </Button>
          }
          data-testid="documents-refresh-error-alert"
          sx={{ mb: 2, maxWidth: 760 }}
        >
          The documents could not be refreshed, so what is shown may be out of date.
        </Alert>
      ) : null}

      {nothingAtAll ? (
        <EmptyState
          title="No documents yet."
          description="Upload a PDF, PNG or JPEG and it will appear here once its scan is clean."
          action={uploadButton('Upload a document', 'documents-upload-first-button')}
          testId="documents"
        />
      ) : (
        <DocumentsTable
          rows={rows}
          loading={list.isPending}
          // Only a changed view dims the table; a poll in the background is silent.
          refreshing={list.isPlaceholderData}
          error={
            list.isError && !list.data && !goneCategory
              ? 'The documents could not be loaded.'
              : null
          }
          onRetry={retry}
          page={params.page}
          total={total}
          onPageChange={turnPage}
          range={params.range}
          writable={writable}
          filtered={filtered}
          onAction={onAction}
        />
      )}

      <UploadDocumentDialog
        key={uploading}
        open={uploadShown && writable}
        onClose={closeUpload}
      />

      <EditDocumentDrawer
        id={editing}
        range={params.range}
        writable={writable}
        onClose={() => setEditing(null)}
      />

      <ReplaceFileDialog
        key={`replace-${replaceCount}`}
        document={replacing}
        open={replaceOpen}
        onClose={() => setReplaceOpen(false)}
      />

      <RemoveDocumentDialog
        document={removing}
        open={removeOpen}
        onCancel={() => setRemoveOpen(false)}
      />
    </Box>
  );
}
